import type React from "react";

import { IconButton } from "@/components/Button/IconButton";
import { ShareToCommunityFeedModal } from "@/components/ShareToCommunityFeedModal/ShareToCommunityFeedModal";
import { Button, type ButtonProps } from "@/design-system/Button";
import { type SupportedDeepLinkEntityType } from "@/helpers/deep-link";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

import type { ContextMenuItem } from "../ContextMenu/ContextMenu";
import { ContextMenu } from "../ContextMenu/ContextMenu";
import { Share06Icon } from "../Icons/Share06Icon";
import { ShareToChatModal } from "../ShareToChatModal/ShareToChatModal";
import { useShareEntityMenuItems } from "./helpers";

interface ShareButtonProps {
  entityType: SupportedDeepLinkEntityType;
  entityId: string;
  otherIds?: {
    folderId?: string;
    groupTypeId?: string;
  };
  variant?: Exclude<ButtonProps["variant"], "filled" | "outline" | "subtle" | "ghostSecondary" | "ai">;
  extraItems?: ContextMenuItem[];
}

export function ShareButton({
  entityId,
  entityType,
  otherIds,
  variant = "soft",
  extraItems,
}: ShareButtonProps): React.ReactNode {
  const [isShareToCommunityFeedModalOpened, shareToCommunityFeedModalHandler] = useBool(false);
  const [isShareToChatModalOpened, shareToChatModalHandler] = useBool(false);

  const { t } = useTranslation();
  const baseMenuItems = useShareEntityMenuItems({
    deepLinkPayload: { entityId, entityType, folderId: otherIds?.folderId, groupTypeId: otherIds?.groupTypeId },
    callbacks: {
      shareToCommunityFeed: shareToCommunityFeedModalHandler.setTrue,
      shareToChat: shareToChatModalHandler.setTrue,
    },
  });

  const menuItems = extraItems ? [...baseMenuItems, ...extraItems] : baseMenuItems;

  return (
    <>
      {variant === "ghostPrimary" && (
        <ContextMenu
          placement="bottom"
          triggerComponent={
            <Button data-testid="share-btn" variant="ghostPrimary" icon={<Share06Icon />}>
              {t("common.action.share")}
            </Button>
          }
          items={menuItems}
        />
      )}
      {variant === "soft" && (
        <ContextMenu
          items={menuItems}
          triggerComponent={
            <IconButton
              data-testid="share-btn"
              type="button"
              variant="soft"
              icon={<Share06Icon />}
              title={t("common.action.share")}
              withTooltip={false}
            />
          }
        />
      )}
      <ShareToCommunityFeedModal
        isOpened={isShareToCommunityFeedModalOpened}
        onOpenChange={shareToCommunityFeedModalHandler.set}
        entityType={entityType}
        entityId={entityId}
        otherIds={otherIds}
      />
      <ShareToChatModal
        isOpened={isShareToChatModalOpened}
        onOpenChange={shareToChatModalHandler.set}
        entityType={entityType}
        entityId={entityId}
        otherIds={otherIds}
      />
    </>
  );
}
